import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { promptOutOfCredits } from "./clubImportPrompt";
import { isKnownInsufficientCredits } from "./permissionsSelectors";
/**
 * Point-of-spend guard: answers "may this call go out" before it goes out.
 *
 * There is no reason to spend a round trip on a call the backend is certain to
 * refuse, and the 402 path stays as the backstop for the balance we can't see
 * (another device spending in parallel, a price change mid-session).
 *
 * ```ts
 * const gate = useCreditGate("solve-sbc-club");
 * if (!gate({ qualifying: usesClubPool(playersFrom) })) return;
 * dispatch(solveSbc(id));
 * ```
 */
export const useCreditGate = (featureId) => {
    const dispatch = useDispatch();
    const store = useStore();
    return useCallback(({ qualifying = true, prompt = true, sbcId, } = {}) => {
        if (!qualifying)
            return true;
        if (sbcId !== undefined &&
            store.getState().permissionsReducer.paidSbcIds.includes(sbcId))
            return true;
        /*
          Read at call time instead of subscribing with useSelector: the balance
          moves on every metered response, and nothing on screen should re-render
          because a button *could* have been refused.
        */
        if (!isKnownInsufficientCredits(store.getState(), featureId))
            return true;
        if (prompt)
            dispatch(promptOutOfCredits(featureId));
        return false;
    }, [dispatch, store, featureId]);
};
//# sourceMappingURL=useCreditGate.js.map